import { CSV_SPLIT_REASON_PREFIX } from "@/lib/csv-split-allocations";

/**
 * Map an emitter `reason` string through the shared tools namespace instead of
 * leaking the parser's English into every locale. Unknown future reasons fall
 * through verbatim so a new diagnostic stays visible rather than blank.
 */
export function translatedCsvReason(
  t: (key: string, substitutions?: Record<string, string>) => string,
  reason: string,
): string {
  if (reason === "no date column mapped") {
    return t("tools.csvWidget.errors.reasons.noDateColumn");
  }
  if (reason === "no amount column mapped") {
    return t("tools.csvWidget.errors.reasons.noAmountColumn");
  }
  if (reason.startsWith("invalid account: ")) {
    return t("tools.csvWidget.errors.reasons.invalidAccount", {
      value: reason.slice("invalid account: ".length),
    });
  }
  if (reason.startsWith("invalid currency: ")) {
    return t("tools.csvWidget.errors.reasons.invalidCurrency", {
      value: reason.slice("invalid currency: ".length),
    });
  }
  // Split reasons carry the model's English detail; the split editor shows
  // the localized, per-field version, so the row list only points there.
  if (reason.startsWith(CSV_SPLIT_REASON_PREFIX)) {
    return t("toolsCsvSplit.rowError");
  }
  if (reason === "unrecognized amount") {
    return t("tools.csvWidget.errors.reasons.badAmount");
  }
  if (reason === "unrecognized date format") {
    return t("tools.csvWidget.errors.reasons.badDate");
  }
  if (reason === "empty date") {
    return t("tools.csvWidget.errors.reasons.emptyDate");
  }
  if (reason === "out-of-range date") {
    return t("tools.csvWidget.errors.reasons.dateOutOfRange");
  }
  if (reason === "unknown month") {
    return t("tools.csvWidget.errors.reasons.unknownMonth");
  }
  if (reason === "invalid description rules") {
    return t("toolsCsvRules.errors.badRules");
  }
  if (reason === "invalid description rule") {
    return t("toolsCsvRules.errors.badRule");
  }
  if (reason === "invalid description rule id") {
    return t("toolsCsvRules.errors.badRuleId");
  }
  if (reason.startsWith("invalid description rule flag: ")) {
    return t("toolsCsvRules.errors.badRuleFlag", {
      id: reason.slice("invalid description rule flag: ".length),
    });
  }
  if (reason.startsWith("invalid description rule operator: ")) {
    return t("toolsCsvRules.errors.badRuleOperator", {
      id: reason.slice("invalid description rule operator: ".length),
    });
  }
  if (reason.startsWith("invalid description rule pattern: ")) {
    return t("toolsCsvRules.errors.badRulePattern", {
      id: reason.slice("invalid description rule pattern: ".length),
    });
  }
  if (reason.startsWith("duplicate description rule id: ")) {
    return t("toolsCsvRules.errors.duplicateRuleId", {
      id: reason.slice("duplicate description rule id: ".length),
    });
  }
  const tooMany = /^too many description rules \(max (\d+)\)$/.exec(reason);
  if (tooMany) {
    return t("toolsCsvRules.errors.tooManyRules", {
      max: tooMany[1]!,
    });
  }
  return reason;
}
